import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, RotateCcw, XCircle, MapPin, Loader2, Calendar, Package, CreditCard, Landmark, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { ordersAPI, paymentsAPI, settingsAPI } from '@/lib/api';
import { googleMapsLink } from '@/lib/maps';
import { toast } from '@/components/ui/sonner';
import { OrderProgressTracker } from '@/components/OrderProgressTracker';
import { statusLabel, normalizeStatus, normalizePayment, CANCELLABLE } from '@/lib/orderStatus';
import { formatUnitSize } from '@/lib/units';
import { money } from '@/lib/format';

export default function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { addItem } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'invoice' | 'reorder' | 'cancel' | null
  // Which payment methods are currently enabled in Admin > Settings.
  const [paymentMethods, setPaymentMethods] = useState({ card: true, eft: false });

  useEffect(() => {
    settingsAPI.getPublic()
      .then(r => setPaymentMethods({ card: true, eft: false, ...(r.data?.payment_methods || {}) }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    let active = true;
    ordersAPI.get(id)
      .then(r => { if (active) { setOrder(r.data); setLoading(false); } })
      .catch(() => { if (active) { setOrder(null); setLoading(false); } });
    return () => { active = false; };
  }, [id, user]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/auth" />;

  const downloadInvoice = async () => {
    setBusy('invoice');
    try {
      const res = await ordersAPI.downloadInvoice(id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `AVZ_Invoice_${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download invoice');
    } finally {
      setBusy(null);
    }
  };

  const reorder = async () => {
    setBusy('reorder');
    try {
      const res = await ordersAPI.reorder(id);
      const { available, unavailable } = res.data;
      available.forEach(it => addItem({ id: it.product_id, ...it }, it.quantity));
      if (available.length) {
        toast.success(`${available.length} item${available.length > 1 ? 's' : ''} added to cart`);
      }
      if (unavailable.length) {
        const names = unavailable.map(u => u.name).join(', ');
        toast.warning(`Not added (window closed or unavailable): ${names}`);
      }
      if (!available.length && !unavailable.length) {
        toast.error('Nothing to reorder');
      }
      if (available.length) navigate('/cart');
    } catch {
      toast.error('Reorder failed');
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this order?')) return;
    setBusy('cancel');
    try {
      await ordersAPI.cancel(id);
      toast.success('Order cancelled');
      const updated = await ordersAPI.get(id);
      setOrder(updated.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    } finally {
      setBusy(null);
    }
  };

  const payNow = async () => {
    setBusy('pay');
    try {
      const res = await paymentsAPI.startPaystackCheckout(id);
      const url = res.data?.redirect_url;
      if (!url) {
        toast.error('Could not start payment.');
        setBusy(null);
        return;
      }
      window.location.assign(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start payment');
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="h-8 w-48 bg-muted rounded animate-pulse mb-6" />
        <div className="h-32 bg-muted rounded-2xl animate-pulse mb-6" />
        <div className="h-64 bg-muted rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12 text-center">
        <p className="font-body text-foreground/60 mb-6">Order not found.</p>
        <Link to="/profile">
          <Button variant="ctaSecondary" className="rounded-full">Back to My Account</Button>
        </Link>
      </div>
    );
  }

  const status = normalizeStatus(order.status);
  const canCancel = CANCELLABLE.includes(status);
  const paymentStatus = normalizePayment(order.payment_status);
  const isUnpaid = ['unpaid', 'payment_requested', 'failed'].includes(paymentStatus);
  const isClosed = ['cancelled', 'closed', 'rejected'].includes(status);
  // Type A (on_checkout) is payable from checkout; Type B (after_confirmation)
  // only once an admin has set payment to "Payment Requested".
  const workflow = order.payment_workflow === 'after_confirmation' ? 'after_confirmation' : 'on_checkout';
  const paymentRequested = paymentStatus === 'payment_requested' || paymentStatus === 'failed';
  const canPay = isUnpaid && !isClosed && (workflow === 'on_checkout' || paymentRequested);
  // Awaiting confirmation = Type B order not yet asked to pay.
  const awaitingConfirmation = isUnpaid && !isClosed && workflow === 'after_confirmation' && !paymentRequested;
  // Honour the order's chosen method, but fall back to whatever is actually
  // enabled in Admin > Settings > Payment Methods (e.g. if card/Paystack is
  // switched off, an unpaid order must be settled via EFT, not Paystack).
  const orderMethod = order.payment_method === 'eft' ? 'eft' : 'card';
  let effectiveMethod = orderMethod;
  if (orderMethod === 'card' && !paymentMethods.card && paymentMethods.eft) effectiveMethod = 'eft';
  if (orderMethod === 'eft' && !paymentMethods.eft && paymentMethods.card) effectiveMethod = 'card';
  const isEft = effectiveMethod === 'eft';
  const placedAt = new Date(order.created_at).toLocaleString('en-ZA', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  return (
    <div data-testid="order-detail-page" className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-8 sm:py-12">
        <Link
          to="/profile"
          data-testid="order-detail-back"
          className="inline-flex items-center gap-1.5 font-body text-xs uppercase tracking-wider text-foreground/50 hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={2} /> Back to My Account
        </Link>

        {/* Header */}
        <div className="mb-8">
          <p className="font-body text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gold font-bold mb-2">Order</p>
          <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1 mb-1">
            <h1
              data-testid="order-detail-number"
              className="font-heading text-2xl sm:text-3xl font-light text-foreground tracking-tight"
            >
              {order.order_number || `#${order.id.slice(0, 8).toUpperCase()}`}
            </h1>
            <p className="font-body text-xs sm:text-sm text-foreground/50 inline-flex items-center gap-1.5">
              <Calendar className="h-4 w-4" strokeWidth={2} /> {placedAt}
            </p>
          </div>
        </div>

        {/* Payment status banner */}
        <div
          data-testid="order-detail-payment-banner"
          className={
            paymentStatus === 'paid'
              ? 'p-4 sm:p-5 rounded-2xl bg-teal/10 border border-teal/30 mb-6 flex items-center justify-between gap-3'
              : 'p-4 sm:p-5 rounded-2xl bg-gold/10 border border-gold/30 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3'
          }
        >
          <div className="flex items-center gap-3 min-w-0">
            {paymentStatus === 'paid' ? (
              <CheckCircle className="h-6 w-6 text-teal flex-shrink-0" strokeWidth={2} />
            ) : (
              <AlertCircle className="h-6 w-6 text-gold flex-shrink-0" strokeWidth={2} />
            )}
            <div className="min-w-0">
              <p
                data-testid="order-detail-payment-status"
                className={`font-body text-xs uppercase tracking-wider font-semibold ${paymentStatus === 'paid' ? 'text-teal' : 'text-gold'}`}
              >
                {paymentStatus === 'paid' ? 'Payment Received' : paymentStatus === 'refunded' ? 'Refunded' : awaitingConfirmation ? 'Under Review' : 'Payment Outstanding'}
              </p>
              <p className="font-body text-sm text-foreground/70 mt-0.5">
                {paymentStatus === 'paid'
                  ? `Paid R${money(order.total)} via ${order.payment_method === 'eft' ? 'EFT' : 'Paystack'}.`
                  : awaitingConfirmation
                    ? "No payment required yet — we'll send you a secure payment link by email & WhatsApp once your order is confirmed."
                    : isEft
                    ? `R${money(order.total)} due — settle via EFT bank transfer. View the payment instructions to complete your order.`
                    : `R${money(order.total)} due — finish your card payment via Paystack to confirm this order.`}
              </p>
            </div>
          </div>
          {canPay && (
            isEft ? (
              <Button
                onClick={() => navigate(`/eft/${id}`)}
                data-testid="order-detail-eft-btn"
                variant="ctaPrimary"
                className="rounded-full gap-2 font-body text-sm font-bold py-5 px-6 shadow-md flex-shrink-0"
              >
                <Landmark className="h-5 w-5" strokeWidth={2} />
                View Payment Details
              </Button>
            ) : (
              <Button
                onClick={payNow}
                disabled={busy === 'pay'}
                data-testid="order-detail-pay-now-btn"
                variant="ctaPrimary"
                className="rounded-full gap-2 font-body text-sm font-bold py-5 px-6 shadow-md flex-shrink-0"
              >
                {busy === 'pay' ? <Loader2 className="h-5 w-5 animate-spin" /> : <CreditCard className="h-5 w-5" strokeWidth={2} />}
                Pay Now
              </Button>
            )
          )}
        </div>

        {/* Progress Tracker */}
        <div data-testid="order-detail-tracker" className="p-5 sm:p-8 rounded-2xl bg-card border border-border mb-6">
          <p className="font-body text-[10px] sm:text-xs uppercase tracking-wider text-foreground/50 mb-5 sm:mb-6">
            Status: <span className="text-foreground font-semibold">{statusLabel(order.status)}</span>
          </p>
          <OrderProgressTracker status={order.status} paymentStatus={paymentStatus} variant="full" updatedAt={order.updated_at} />
          {(status === 'ready_for_collection' || status === 'collection_notification_sent' || status === 'closed') && (
            <p className="font-body text-xs sm:text-sm text-purple-300 mt-6 inline-flex items-center gap-1.5">
              <Package className="h-4 w-4" strokeWidth={2} /> {statusLabel(order.status)}
            </p>
          )}
        </div>

        {/* Collection Point */}
        {order.collection_point_name && (
          <div className="p-4 sm:p-5 rounded-2xl bg-teal/10 border border-teal/30 mb-6 flex items-start gap-3">
            <MapPin className="h-5 w-5 text-teal flex-shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <p className="font-body text-[10px] uppercase tracking-wider text-teal font-semibold mb-1">Collection Point</p>
              <p className="font-body text-sm text-foreground">{order.collection_point_name}</p>
              {order.collection_point_address && (
                <p className="font-body text-xs text-foreground/60 mt-0.5">{order.collection_point_address}</p>
              )}
              {googleMapsLink(order.collection_point_address, order.collection_point_maps_url) && (
                <a
                  href={googleMapsLink(order.collection_point_address, order.collection_point_maps_url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="order-cp-maps-link"
                  className="inline-flex items-center gap-1 font-body text-xs text-teal hover:text-teal/80 underline mt-1.5"
                >
                  <MapPin className="h-3 w-3" /> Open in Google Maps
                </a>
              )}
            </div>
          </div>
        )}

        {/* Items grouped by supplier (alphabetical) */}
        <div className="rounded-2xl bg-card border border-border overflow-hidden mb-6" data-testid="order-detail-items">
          {(() => {
            const groups = {};
            (order.items || []).forEach(it => {
              const key = it.supplier_name || 'Unknown supplier';
              if (!groups[key]) groups[key] = [];
              groups[key].push(it);
            });
            const supplierNames = Object.keys(groups).sort((a, b) => a.localeCompare(b));
            return supplierNames.map(supplierName => (
              <div key={supplierName} data-testid={`order-detail-supplier-${supplierName}`}>
                <div className="px-5 py-2.5 bg-gold/8 border-b border-gold/15">
                  <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gold font-semibold">
                    {supplierName}
                  </p>
                </div>
                {groups[supplierName].map((it, i) => (
                  <div
                    key={it.product_id || i}
                    data-testid={`order-detail-item-${i}`}
                    className="px-5 py-3 border-b border-border/50 last:border-0 flex items-center gap-4"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-semibold text-foreground truncate">
                        {it.name}
                        {formatUnitSize(it) && (
                          <span className="ml-2 font-body text-xs font-normal text-gold/80" data-testid={`order-detail-item-unit-${i}`}>
                            {formatUnitSize(it)}
                          </span>
                        )}
                      </p>
                    </div>
                    <p className="font-body text-sm text-foreground/70 w-12 text-center">×{it.quantity}</p>
                    <p className="font-body text-sm font-bold text-foreground w-24 text-right">
                      R{money((it.price * it.quantity))}
                    </p>
                  </div>
                ))}
              </div>
            ));
          })()}

          {/* Subtotal + Delivery + Collection Fee + Total (both fee lines always shown) */}
          <div className="px-5 py-2 border-t border-border flex items-center justify-between bg-muted/10">
            <span className="font-body text-xs text-foreground/60">Subtotal</span>
            <span className="font-body text-sm text-foreground/80">
              R{money((order.items_subtotal ?? (order.total - (order.delivery_total || 0) - (order.collection_fee || 0))))}
            </span>
          </div>
          <div className="px-5 py-2 border-t border-border/40 flex items-center justify-between bg-muted/10" data-testid="order-detail-delivery">
            <span className="font-body text-xs text-foreground/60">Delivery</span>
            <span className="font-body text-sm text-foreground/80">R{money((order.delivery_total || 0))}</span>
          </div>
          <div className="px-5 py-2 border-t border-border/40 flex items-center justify-between bg-muted/10" data-testid="order-detail-collection-fee">
            <span className="font-body text-xs text-foreground/60">Collection Fee</span>
            <span className="font-body text-sm text-foreground/80">R{money((order.collection_fee || 0))}</span>
          </div>
          <div className="px-5 py-4 bg-muted/20 border-t border-border flex items-center justify-between">
            <span className="font-body text-xs uppercase tracking-wider text-foreground/60">Total</span>
            <span
              className="font-heading text-base sm:text-lg font-bold text-gold"
              data-testid="order-detail-total"
            >
              R{money(order.total)}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={downloadInvoice}
            disabled={busy === 'invoice'}
            data-testid="order-detail-invoice-btn"
            variant="ctaSecondary"
            className="rounded-full gap-2 font-body text-sm font-semibold py-5 px-6"
          >
            {busy === 'invoice' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Download className="h-5 w-5" strokeWidth={2} />}
            Download Invoice
          </Button>
          <Button
            onClick={reorder}
            disabled={busy === 'reorder'}
            data-testid="order-detail-reorder-btn"
            variant="ctaPrimary"
            className="rounded-full gap-2 font-body text-sm font-bold py-5 px-6 shadow-md"
          >
            {busy === 'reorder' ? <Loader2 className="h-5 w-5 animate-spin" /> : <RotateCcw className="h-5 w-5" strokeWidth={2} />}
            Reorder
          </Button>
          {canCancel && (
            <Button
              onClick={cancel}
              disabled={busy === 'cancel'}
              data-testid="order-detail-cancel-btn"
              variant="ctaDanger"
              className="rounded-full gap-2 font-body text-sm font-semibold py-5 px-6"
            >
              {busy === 'cancel' ? <Loader2 className="h-5 w-5 animate-spin" /> : <XCircle className="h-5 w-5" strokeWidth={2} />}
              Cancel Order
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
